/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';

const messages = defineMessages({
	disable_definitions_more_icon_tooltip: {
		id: 'search_ai.disable_definitions_more_icon_tooltip',
		defaultMessage: 'See more actions',
		description: 'Tooltip for definition more icon',
	},
	see_more_actions_button_label: {
		id: 'search_ai.definitions_see_more_actions_label',
		defaultMessage: 'See more actions',
		description:
			'Accessible label for the See More actions button in the definitions/reading aids menu',
	},
	disable_definitions_header: {
		id: 'search_ai.disable_definitions_header',
		defaultMessage: 'Don’t recommend definitions at all',
		description:
			'Header text for definitions menu option to disable autohighlighting for all pages',
	},
	disable_definitions_description: {
		id: 'search_ai.disable_definitions_description',
		defaultMessage:
			'When this is on, we’ll no longer automatically highlight terms and acronyms on any page. Only you will see these changes.',
		description:
			'Description text for the reading aids menu option to not disable auto highlighting for all pages',
	},
});

const DisableDefinitionMenuWrapper = css({
	display: 'flex',
	width: '320px',
	flexDirection: 'row',
	justifyContent: 'space-between',
	zIndex: 1,
	marginTop: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.150'),
	marginRight: token('space.075'),
	gap: token('space.250'),
});

const DisableDefinitionMenuTextWrapper = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	width: '244px',
});

const DisableDefinitionMenuHeaderStyles = css({
	font: token('font.body'),
	color: token('color.text'),
});

const DisableDefinitionMenuDescriptionStyles = css({
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
});

const DisableDefinitionMenuToggleWrapper = css({
	display: 'flex',
	width: 'fit-content',
});

const MoreIconStyled = () => (
	<MoreIcon
		label={
			expValEquals('a11y-fixes-week1-june-2026', 'isEnabled', true)
				? ''
				: 'reading-aids-menu-button-icon'
		}
		color={token('color.icon.subtle')}
	/>
);

export const DisableDefinitionsMenu: () => JSX.Element | null = () => {
	const { readingAidsOptions } = useAIAnswerContext();

	const [menuOpen, setMenuOpen] = useState(false);
	const [disableDefinitions, setDisableDefinitions] = useState(false);

	useEffect(() => {
		const definitionsDisabled =
			!!readingAidsOptions?.disableDefinitionsCallbacks?.getDefinitionsDisabled();
		setDisableDefinitions(definitionsDisabled);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { formatMessage } = useIntl();

	const disableDefinitionsCallbacks = readingAidsOptions?.disableDefinitionsCallbacks;

	return disableDefinitionsCallbacks ? (
		<Tooltip
			position="bottom"
			content={formatMessage(messages.disable_definitions_more_icon_tooltip)}
		>
			<DropdownMenu
				isOpen={menuOpen}
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						{...props}
						appearance="subtle"
						icon={MoreIconStyled}
						ref={triggerRef}
						onClick={() => {
							setMenuOpen(!menuOpen);
						}}
						testId="definitions-menu-button"
						label={
							expValEquals('a11y-fixes-week1-june-2026', 'isEnabled', true)
								? formatMessage(messages.see_more_actions_button_label)
								: 'definitions-menu-button'
						}
					/>
				)}
			>
				<div css={DisableDefinitionMenuWrapper}>
					<div css={DisableDefinitionMenuTextWrapper}>
						<div css={DisableDefinitionMenuHeaderStyles}>
							{formatMessage(messages.disable_definitions_header)}
						</div>
						<div css={DisableDefinitionMenuDescriptionStyles}>
							{formatMessage(messages.disable_definitions_description)}
						</div>
					</div>
					<div css={DisableDefinitionMenuToggleWrapper}>
						<Toggle
							onChange={() => {
								if (disableDefinitions) {
									disableDefinitionsCallbacks.onEnableDefinitions();
								} else {
									disableDefinitionsCallbacks.onDisableDefinitions();
								}
								setDisableDefinitions(!disableDefinitions);
							}}
							isChecked={disableDefinitions}
							testId="definitions-toggle-disable"
						/>
					</div>
				</div>
			</DropdownMenu>
		</Tooltip>
	) : null;
};
